import React from 'react';

import styled from 'styled-components';

import { desktop } from '../../styles';
import { useLDFlags } from '../../utils/LD/useLDFlags';

const KlarnaMessagingWrapper = styled.div<{ isDouble?: boolean }>`
  margin-top: ${props => props.theme.spacing.tiny}px;
  margin-left: -${props => props.theme.spacing.large}px;
  padding: 4px ${props => props.theme.spacing.tiny}px 4px
    ${props => props.theme.spacing.large}px;

  ${({ theme }) => theme.paragraph.variants.paragraph4.textStyles}
  font-size: 10px !important;

  color: ${props => props.theme.colors.gunmetal};

  display: flex;
  flex-direction: ${props => (props.isDouble ? 'column' : 'row')};
  justify-content: center;

  ${props =>
    props.theme.context.cartDrawer &&
    `
    font-size: 12px !important;
    margin: 0 auto;
    padding-left: 0;
    padding-right: 0;
    padding-top: ${props.theme.spacing.tiny}px;
  `};

  ${desktop`
    ${props =>
      props.theme.context.cartDrawer &&
      `
        flex-direction: row !important;
        gap: 4px;
      `};
  `}
`;

const KlarnaLogo = styled.img<{ isDouble?: boolean }>`
  height: 20px;
  margin: ${props => (props.isDouble ? '0px 4px 0px 0px' : '0px 4px')};
`;

const AfterpayLogo = styled.img`
  height: 13px;
  margin: 0 4px;

  ${props =>
    props.theme.context.cartDrawer &&
    `
    margin-top: 3px;
  `};
`;

const DoubleLogoContainer = styled.div`
  display: flex;
  align-items: center;

  ${props =>
    props.theme.context.cartDrawer &&
    `
    margin: 0 auto;
  `};
`;

type KlarnaMessagingProps = {
  baseAmount: number;
  justKlarna: boolean;
};

const KlarnaMessaging = ({
  baseAmount,
  justKlarna = false,
}: KlarnaMessagingProps) => {
  const { 'additional-payment-methods': additionalPaymentMethods } =
    useLDFlags();
  const isAfterpayEnabled =
    additionalPaymentMethods === 'afterpay' ||
    additionalPaymentMethods === 'both';
  if (
    (baseAmount < 35 && !isAfterpayEnabled) ||
    (baseAmount < 35 && justKlarna)
  ) {
    // Just Klarna Message
    return (
      <KlarnaMessagingWrapper>
        <KlarnaLogo src="/static/images/klarna-badge.svg" alt="Klarna" />
        <p>available in installments for orders over $35</p>
      </KlarnaMessagingWrapper>
    );
  }

  if (baseAmount < 35 && isAfterpayEnabled) {
    // Klarna + Afterpay Message
    return (
      <KlarnaMessagingWrapper isDouble>
        <p>available in installments for orders over $35 with</p>{' '}
        <DoubleLogoContainer>
          <KlarnaLogo
            isDouble
            src="/static/images/klarna-badge.svg"
            alt="Klarna"
          />
          <p>or </p>
          <AfterpayLogo
            src="/static/images/afterpay-logo-black.svg"
            alt="Afterpay"
          />
        </DoubleLogoContainer>
      </KlarnaMessagingWrapper>
    );
  }

  if (
    (baseAmount >= 35 && !isAfterpayEnabled) ||
    (baseAmount >= 35 && justKlarna)
  ) {
    // Just Klarna Message w/Payments
    return (
      <KlarnaMessagingWrapper>
        <p>or 4 interest free payments by</p>
        <KlarnaLogo src="/static/images/klarna-badge.svg" alt="Klarna" />
      </KlarnaMessagingWrapper>
    );
  }

  if (baseAmount >= 35 && isAfterpayEnabled) {
    // Klarna + Afterpay Message w/Payments
    return (
      <KlarnaMessagingWrapper>
        <p>or 4 interest free payments by</p>
        <DoubleLogoContainer>
          <KlarnaLogo src="/static/images/klarna-badge.svg" alt="Klarna" />
          <p>or</p>
          <AfterpayLogo
            src="/static/images/afterpay-logo-black.svg"
            alt="Afterpay"
          />
        </DoubleLogoContainer>
      </KlarnaMessagingWrapper>
    );
  }
  return null;
};

export default KlarnaMessaging;
